import React, { useEffect, useState, useRef } from 'react';
import { useHospitalSelection } from '../../hooks/useHospitalSelection';
import { usePersistedState } from '../../hooks/usePersistedState';
import LineChart from '../../components/Charts/LineChart';
import CustomSelect from '../../components/Common/CustomSelect';
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice';
import { TmbPillSelect } from '../../components/TMBControls';
import { generatePDFIds } from '../../utils/pdfIdHelper';
import { parseMonthToValue, safeNumber } from '../../utils/dataUtils';
import html2canvas  from "html2canvas"
import '../../styles/dropdown.css';
const ANCHOR_YEAR = 2025;

const MONTHLY_START_YYYYMM = 202601;
const MONTHLY_END_YYYYMM = 202612;

const Y2_START_YYYYMM = 202509;
const Y2_END_YYYYMM = 202608;
const Y2_LABEL = 'Y2 (Sep 2025 - Aug 2026)';
const Y2_CATEGORIES = ['', Y2_LABEL, ''];
const y2Data = (v: number | null): (number | null)[] => [null, v, null];

const BASE_MAX_NTSV_RACE_SELECTIONS = 6;
const DEFAULT_NTSV_RACE_SELECTIONS = ['Hispanic', 'Asian', 'Black/African American', 'Other', 'NH White'];

const NTSV_RACE_COLORS: Record<string, string> = {
    'BIPOC':                  '#F2E500',
    'Hispanic':               '#0072B2',
    'Asian':                  '#009E73',
    'Black/African American': '#E69F00',
    'Other':                  '#CC79A7',
    'NH White':               '#56B4E9',
};

function parseQuarterlyPeriod(raw: any): { year: number; quarter: number } | null {
    const s = String(raw ?? '').trim();
    let m = s.match(/Q\s*(\d)\s+(\d{4})/i);
    if (m) return { quarter: parseInt(m[1], 10), year: parseInt(m[2], 10) };
    m = s.match(/(\d{4})\s*[- ]?\s*Q\s*(\d)/i);
    if (m) return { year: parseInt(m[1], 10), quarter: parseInt(m[2], 10) };
    m = s.match(/Q\s*(\d)\s*[- ]\s*(\d{4})/i);
    if (m) return { quarter: parseInt(m[1], 10), year: parseInt(m[2], 10) };
    return null;
}

function quarterlySortKey(raw: any): number {
    const p = parseQuarterlyPeriod(raw);
    if (!p) return 0;
    return p.year * 10 + p.quarter;
}

function quarterlyInTicketRange(raw: any): boolean {
    const p = parseQuarterlyPeriod(raw);
    if (!p) return true;
    const k = p.year * 10 + p.quarter;
    return k >= 20251 && k <= 20253;
}

function parseBiYearlyPeriod(raw: any): { year: number; secondHalf: boolean } | null {
    const s = String(raw ?? '').trim();
    const m = s.match(/(Jan-Jun|Jul-Dec)\s+(\d{4})/i);
    if (!m) return null;
    return { year: parseInt(m[2], 10), secondHalf: /^jul/i.test(m[1]) };
}

function biYearlySortKey(raw: any): number {
    const p = parseBiYearlyPeriod(raw);
    if (!p) return 0;
    return p.year * 2 + (p.secondHalf ? 1 : 0);
}

function biYearlyOnOrAfterSep2025(raw: any): boolean {
    const p = parseBiYearlyPeriod(raw);
    if (!p) return true;
    if (p.year > ANCHOR_YEAR) return true;
    if (p.year < ANCHOR_YEAR) return false;
    return p.secondHalf;
}

function yearlySortKey(raw: any): number {
    const y = parseInt(String(raw ?? '').trim(), 10);
    return Number.isFinite(y) ? y : 0;
}

function yearlyOnOrAfterSep2025(raw: any): boolean {
    const y = parseInt(String(raw ?? '').trim(), 10);
    if (!Number.isFinite(y)) return true;
    return y >= ANCHOR_YEAR;
}

function periodOnOrAfterSep2025(periodType: string, periodLabel: any): boolean {
    switch (periodType) {
        case 'quarterly': return quarterlyInTicketRange(periodLabel);
        case 'bi-yearly': return biYearlyOnOrAfterSep2025(periodLabel);
        case 'yearly': return yearlyOnOrAfterSep2025(periodLabel);
        default: return true;
    }
}

function expandQuarterlyCategories(existingLabels: string[]): string[] {
    const keys = existingLabels.map(parseQuarterlyPeriod).filter(Boolean) as { year: number; quarter: number }[];
    if (keys.length === 0) return existingLabels;
    const RANGE_START = 2025 * 4 + 0;
    const RANGE_END = 2025 * 4 + 2;
    const out: string[] = [];
    for (let o = RANGE_START; o <= RANGE_END; o++) {
        out.push(`Q${(o % 4) + 1} ${Math.floor(o / 4)}`);
    }
    return out;
}

function expandBiYearlyCategories(existingLabels: string[]): string[] {
    const parsed = existingLabels.map(parseBiYearlyPeriod).filter(Boolean) as { year: number; secondHalf: boolean }[];
    if (parsed.length === 0) return existingLabels;
    let maxOrd = ANCHOR_YEAR * 2 + 1;
    for (const p of parsed) {
        const ord = p.year * 2 + (p.secondHalf ? 1 : 0);
        if (ord > maxOrd) maxOrd = ord;
    }
    const startOrd = ANCHOR_YEAR * 2 + 1;
    const out: string[] = [];
    for (let ord = startOrd; ord <= maxOrd; ord++) {
        const y = Math.floor(ord / 2);
        const secondHalf = ord % 2 === 1;
        out.push(secondHalf ? `Jul-Dec ${y}` : `Jan-Jun ${y}`);
    }
    return out.length ? out : existingLabels;
}

function canonicalPeriodLabel(periodType: string, raw: any): string {
    if (periodType === 'quarterly') {
        const p = parseQuarterlyPeriod(raw);
        return p ? `Q${p.quarter} ${p.year}` : String(raw ?? '');
    }
    if (periodType === 'bi-yearly') {
        const p = parseBiYearlyPeriod(raw);
        if (!p) return String(raw ?? '');
        return p.secondHalf ? `Jul-Dec ${p.year}` : `Jan-Jun ${p.year}`;
    }
    if (periodType === 'yearly') {
        const y = parseInt(String(raw ?? '').trim(), 10);
        return Number.isFinite(y) ? String(y) : String(raw ?? '');
    }
    return String(raw ?? '');
}

function expandYearlyCategories(existingLabels: string[]): string[] {
    const years = existingLabels.map(y => parseInt(String(y).trim(), 10)).filter(n => Number.isFinite(n));
    if (years.length === 0) return existingLabels;
    const minY = Math.min(...years);
    const maxY = Math.max(...years);
    const startY = Math.max(minY, ANCHOR_YEAR);
    const out: string[] = [];
    for (let y = startY; y <= maxY; y++) out.push(String(y));
    return out.length ? out : existingLabels;
}

function poolY2Monthly(rows: any[], monthIdx: number, reportTypeIdx: number, numIdx: number, denIdx: number): { num: number; den: number; pct: number } | null {
    let num = 0, den = 0, any = false;
    for (const r of rows) {
        if (String(r[reportTypeIdx]) !== 'Monthly') continue;
        const mv = parseMonthToValue(r[monthIdx]);
        if (mv < Y2_START_YYYYMM || mv > Y2_END_YYYYMM) continue;
        const d = safeNumber(r[denIdx]);
        if (d === null || d === 0) continue;
        const n = safeNumber(r[numIdx]);
        den += d;
        if (n !== null) num += n;
        any = true;
    }
    return any && den > 0 ? { num, den, pct: Math.round((num / den) * 100 * 100) / 100 } : null;
}

import { exportToCSV } from '../../utils/exportUtils';
import './OutcomeMeasures.css';

const OutcomeMeasures: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection();
    const [chartData, setChartData] = useState<any>({
        chart1: { categories: [], series: [] },
        chart2: { categories: [], series: [] },
        chart3: { categories: [], series: [] },
        chart4: { categories: [], series: [] }
    });
    const [tableData, setTableData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [timePeriod, setTimePeriod] = usePersistedState<string>('outcome_measures_timePeriod', 'monthly');
    const [rawChartData, setRawChartData] = useState<any>(null);
    const [rawTableData, setRawTableData] = useState<any>(null);
    const [rawRaceEthnicityData, setRawRaceEthnicityData] = useState<any>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    const chart1Ref = useRef<any>(null);
    const chart2Ref = useRef<any>(null);
    const chart3Ref = useRef<any>(null);
    const chart4Ref = useRef<any>(null);
    const chart5Ref = useRef<any>(null);

    const [screeningChartData, setScreeningChartData] = useState<any>({ categories: [], series: [] });
    const [rawScreeningData, setRawScreeningData] = useState<any>(null);

    const [selectedRaceGroups, setSelectedRaceGroups] = usePersistedState<string[]>('outcome_ntsv_race_groups', DEFAULT_NTSV_RACE_SELECTIONS);
    const [availableRaceGroups, setAvailableRaceGroups] = useState<string[]>([]);
    const [raceDropdownOpen, setRaceDropdownOpen] = useState(false);

    useEffect(() => {
        if (selectedHospitalIds.length === 0) {
            setChartData({
                chart1: { categories: [], series: [] },
                chart2: { categories: [], series: [] },
                chart3: { categories: [], series: [] },
                chart4: { categories: [], series: [] }
            });
            setTableData([]);
            setRawChartData(null);
            setRawTableData(null);
            setRawRaceEthnicityData(null);
            setRawScreeningData(null);
            setInitialLoading(false);
            return;
        }

        setLoading(true);
        const hospitalIdsParam = selectedHospitalIds.join(',');
        const chartUrl = `/api/outcome/${hospitalIdsParam}`;
        const tableUrl = `/api/outcome_data_table/${hospitalIdsParam}`;
        const raceEthnicityUrl = `/api/outcome_race_ethnicity/${hospitalIdsParam}`;
        const screeningUrl = `/api/pmh/process/screening/${hospitalIdsParam}`;

        Promise.all([
            fetch(chartUrl).then(res => res.ok ? res.json() : null),
            fetch(tableUrl).then(res => res.ok ? res.json() : null),
            fetch(raceEthnicityUrl).then(res => res.ok ? res.json() : null),
            fetch(screeningUrl).then(res => res.ok ? res.json() : null)
        ]).then(([chartRes, tableRes, raceEthnicityRes, screeningRes]) => {
            setRawChartData(chartRes);
            setRawTableData(tableRes);
            setRawRaceEthnicityData(raceEthnicityRes);
            setRawScreeningData(screeningRes);
            setLoading(false);
            setInitialLoading(false);
        }).catch(err => {
            console.error("Error fetching outcome measures:", err);
            setLoading(false);
            setInitialLoading(false);
        });

    }, [selectedHospitalIds, refreshKey]);

    useEffect(() => {
        if (rawChartData === null && rawTableData === null) return;

        if (timePeriod === 'y2') {
            buildY2Charts();
            buildY2Table();
            return;
        }

        if (isMultiSelect) {
            processMultiSelectData(rawChartData, timePeriod);
        } else {
            processSingleSelectData(rawChartData, timePeriod);
        }

        if (rawTableData) {
            processTableData(rawTableData, timePeriod);
        } else {
            setTableData([]);
        }
    }, [timePeriod, rawChartData, rawTableData, isMultiSelect]);

    useEffect(() => {
        if (!rawRaceEthnicityData) return

        const rows = rawRaceEthnicityData?.response?.result?.rows || []
        const columns = rawRaceEthnicityData?.response?.result?.column_order || []

        if (rows.length === 0) {
            setChartData((prev: any) => ({ ...prev, chart2: { categories: [], series: [] } }))
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const colMonth = columns.indexOf('Month') !== -1 ? columns.indexOf('Month') : 3
        const colReportType = columns.indexOf('report_type') !== -1 ? columns.indexOf('report_type') : 4
        const colGroup = columns.indexOf('group') !== -1 ? columns.indexOf('group') : 5
        const colNumerator = columns.indexOf('numerator') !== -1 ? columns.indexOf('numerator') : 6
        const colDenominator = columns.indexOf('denominator') !== -1 ? columns.indexOf('denominator') : 7
        const colPercentage = columns.indexOf('percentage') !== -1 ? columns.indexOf('percentage') : 8
        const colRaceType = columns.indexOf('race_ethnicity_type') !== -1 ? columns.indexOf('race_ethnicity_type') : 9

        // Extract available race groups from data for the dropdown
        const groupSet = new Set<string>()
        rows.forEach((r: any) => {
            const raceVal = String(r[colGroup] ?? '').trim()
            const raceType = String(r[colRaceType] ?? '').trim().toLowerCase()
            if (raceType === 'binary') {
                if (raceVal === 'All Other Groups') groupSet.add('BIPOC')
            } else {
                if (raceVal === 'Black') groupSet.add('Black/African American')
                else if (raceVal) groupSet.add(raceVal)
            }
        })
        const preferredOrder = ['BIPOC', 'Hispanic', 'Asian', 'Black/African American', 'Other', 'NH White']
        const available = preferredOrder.filter(r => groupSet.has(r))
        const remaining = Array.from(groupSet).filter(r => !preferredOrder.includes(r)).sort()
        setAvailableRaceGroups([...available, ...remaining])

        if (timePeriod === 'y2') {
            const groups = selectedRaceGroups.length > 0 ? selectedRaceGroups : DEFAULT_NTSV_RACE_SELECTIONS
            const resolveY2Label = (row: any[]): string | null => {
                const raceVal = String(row[colGroup] ?? '').trim()
                const raceType = String(row[colRaceType] ?? '').trim().toLowerCase()
                if (raceType === 'binary') {
                    return raceVal === 'All Other Groups' && groups.includes('BIPOC') ? 'BIPOC' : null
                }
                const displayLabel = raceVal === 'Black' ? 'Black/African American' : raceVal
                return groups.includes(displayLabel) ? displayLabel : null
            }
            const series = groups.map(group => {
                let num = 0, den = 0, any = false
                rows.forEach((row: any[]) => {
                    if (String(row[colReportType]) !== 'Monthly') return
                    if (resolveY2Label(row) !== group) return
                    const mv = parseMonthToValue(row[colMonth])
                    if (mv < Y2_START_YYYYMM || mv > Y2_END_YYYYMM) return
                    const n = parseFloat(row[colNumerator]); const d = parseFloat(row[colDenominator])
                    if (isNaN(n) || isNaN(d) || d === 0) return
                    num += n; den += d; any = true
                })
                return { name: group, data: y2Data(any && den > 0 ? Math.round((num / den) * 100 * 100) / 100 : null) }
            })
            setChartData((prev: any) => ({ ...prev, chart2: { categories: Y2_CATEGORIES, series } }))
            return
        }

        const filtered = rows.filter((row: any[]) => row[colReportType] === intervalMap[timePeriod])

        if (filtered.length === 0) {
            setChartData((prev: any) => ({ ...prev, chart2: { categories: [], series: [] } }))
            return
        }

        const groups = selectedRaceGroups.length > 0 ? selectedRaceGroups : DEFAULT_NTSV_RACE_SELECTIONS
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

        // Resolve a row to a display label based on race_ethnicity_type
        const resolveLabel = (row: any[]): string | null => {
            const raceVal = String(row[colGroup] ?? '').trim()
            const raceType = String(row[colRaceType] ?? '').trim().toLowerCase()
            if (raceType === 'binary') {
                return raceVal === 'All Other Groups' && groups.includes('BIPOC') ? 'BIPOC' : null
            }
            const displayLabel = raceVal === 'Black' ? 'Black/African American' : raceVal
            return groups.includes(displayLabel) ? displayLabel : null
        }

        const buildExpandedCategories = (allPeriods: string[]) => {
            if (timePeriod === 'monthly') {
                const monthVals = allPeriods.map(k => parseMonthToValue(k)).filter(v => v >= MONTHLY_START_YYYYMM && v <= MONTHLY_END_YYYYMM)
                if (monthVals.length === 0) return []
                const minVal = MONTHLY_START_YYYYMM
                const maxVal = Math.max(...monthVals)
                const allMonths: string[] = []
                for (let ym = minVal; ym <= maxVal;) {
                    const y = Math.floor(ym / 100)
                    const m = ym % 100
                    allMonths.push(`${monthNames[m - 1]} ${y}`)
                    if (m === 12) ym = (y + 1) * 100 + 1
                    else ym = y * 100 + (m + 1)
                }
                return allMonths
            } else if (timePeriod === 'quarterly') {
                return expandQuarterlyCategories(allPeriods.sort((a, b) => quarterlySortKey(a) - quarterlySortKey(b)))
            } else if (timePeriod === 'bi-yearly') {
                return expandBiYearlyCategories(allPeriods.sort((a, b) => biYearlySortKey(a) - biYearlySortKey(b)))
            } else {
                return expandYearlyCategories(allPeriods.sort((a, b) => yearlySortKey(a) - yearlySortKey(b)))
            }
        }

        if (isMultiSelect) {
            const aggregated: Record<string, Record<string, { numSum: number, denSum: number }>> = {}

            filtered.forEach((row: any[]) => {
                const rawPeriod = String(row[colMonth] ?? '')
                const num = parseFloat(row[colNumerator])
                const den = parseFloat(row[colDenominator])
                const label = resolveLabel(row)

                if (!rawPeriod || !label || isNaN(num) || isNaN(den)) return

                const period = timePeriod !== 'monthly'
                    ? canonicalPeriodLabel(timePeriod, rawPeriod)
                    : rawPeriod

                if (!aggregated[period]) aggregated[period] = {}
                if (!aggregated[period][label]) aggregated[period][label] = { numSum: 0, denSum: 0 }
                aggregated[period][label].numSum += num
                aggregated[period][label].denSum += den
            })

            const expandedCategories = buildExpandedCategories(Object.keys(aggregated))

            const series = groups.map(group => ({
                name: group,
                data: expandedCategories.map(period => {
                    const d = aggregated[period]?.[group]
                    if (!d || d.denSum === 0) return null
                    return Math.round((d.numSum / d.denSum) * 100 * 100) / 100
                })
            }))

            setChartData((prev: any) => ({
                ...prev,
                chart2: { categories: expandedCategories, series }
            }))
        } else {
            const aggregated: Record<string, Record<string, { numSum: number, denSum: number }>> = {}

            filtered.forEach((row: any[]) => {
                const rawPeriod = String(row[colMonth] ?? '')
                const num = parseFloat(row[colNumerator])
                const den = parseFloat(row[colDenominator])
                const label = resolveLabel(row)

                if (!rawPeriod || !label || isNaN(num) || isNaN(den)) return

                const period = timePeriod !== 'monthly'
                    ? canonicalPeriodLabel(timePeriod, rawPeriod)
                    : rawPeriod

                if (!aggregated[period]) aggregated[period] = {}
                if (!aggregated[period][label]) aggregated[period][label] = { numSum: 0, denSum: 0 }
                aggregated[period][label].numSum += num
                aggregated[period][label].denSum += den
            })

            const expandedCategories = buildExpandedCategories(Object.keys(aggregated))

            const series = groups.map(group => ({
                name: group,
                data: expandedCategories.map(period => {
                    const d = aggregated[period]?.[group]
                    if (!d || d.denSum === 0) return null
                    return Math.round((d.numSum / d.denSum) * 100 * 100) / 100
                })
            }))

            setChartData((prev: any) => ({
                ...prev,
                chart2: { categories: expandedCategories, series }
            }))
        }
    }, [rawRaceEthnicityData, timePeriod, isMultiSelect, selectedRaceGroups])

    useEffect(() => {
        if (!rawScreeningData) {
            setScreeningChartData({ categories: [], series: [] })
            return
        }

        const rows = rawScreeningData?.response?.result?.rows || []
        const columns = rawScreeningData?.response?.result?.column_order || []

        if (rows.length === 0) {
            setScreeningChartData({ categories: [], series: [] })
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const reportTypeIdx = columns.indexOf('report_type') !== -1 ? columns.indexOf('report_type') : 2
        const stratIdx = columns.indexOf('stratification') !== -1 ? columns.indexOf('stratification') : 3
        const stratValueIdx = columns.indexOf('strat_value') !== -1 ? columns.indexOf('strat_value') : 4

        if (timePeriod === 'y2') {
            const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2
            let anxNum = 0, depNum = 0, den = 0, any = false
            rows.forEach((row: any[]) => {
                if (row[reportTypeIdx] !== 'Monthly' || row[stratIdx] !== 'Overall' || row[stratValueIdx] !== 'All Patients') return
                const raw = String(row[periodIdx])
                if (/baseline/i.test(raw)) return
                const mv = parseMonthToValue(raw)
                if (mv < Y2_START_YYYYMM || mv > Y2_END_YYYYMM) return
                const d = safeNumber(row[5])
                if (d === null || d === 0) return
                const a = safeNumber(row[6]); const dp = safeNumber(row[7])
                den += d; if (a !== null) anxNum += a; if (dp !== null) depNum += dp; any = true
            })
            setScreeningChartData({
                categories: any && den > 0 ? Y2_CATEGORIES : [],
                series: [
                    { name: 'Anxiety Screening', data: y2Data(any && den > 0 ? Math.round((anxNum / den) * 100 * 100) / 100 : null) },
                    { name: 'Depression Screening', data: y2Data(any && den > 0 ? Math.round((depNum / den) * 100 * 100) / 100 : null) }
                ]
            })
            return
        }

        const filteredRows = rows.filter((row: any[]) => {
            return row[reportTypeIdx] === intervalMap[timePeriod] &&
                row[stratIdx] === 'Overall' &&
                row[stratValueIdx] === 'All Patients'
        })

        if (filteredRows.length === 0) {
            setScreeningChartData({ categories: [], series: [] })
            return
        }

        const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2

        const aggregatedData: Record<string, {
            anxietyNum: number, depressionNum: number, denominator: number
        }> = {}

        filteredRows.forEach((row: any[]) => {
            const rawPeriod = String(row[periodIdx])
            if (!rawPeriod) return

            const denom = safeNumber(row[5])
            const anxNum = safeNumber(row[6])
            const depNum = safeNumber(row[7])

            if (denom === null || denom === 0) return

            const period = /baseline/i.test(rawPeriod)
                ? rawPeriod
                : (timePeriod !== 'monthly' ? canonicalPeriodLabel(timePeriod, rawPeriod) : rawPeriod)

            if (!aggregatedData[period]) {
                aggregatedData[period] = { anxietyNum: 0, depressionNum: 0, denominator: 0 }
            }
            aggregatedData[period].denominator += denom
            if (anxNum !== null) aggregatedData[period].anxietyNum += anxNum
            if (depNum !== null) aggregatedData[period].depressionNum += depNum
        })

        const allKeys = Object.keys(aggregatedData)
        const baselineKeys = allKeys.filter(k => /baseline/i.test(k))
        const regularKeys = allKeys.filter(k => !/baseline/i.test(k))

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

        let expandedCategories: string[]

        if (timePeriod === 'monthly') {
            const monthVals = regularKeys.map(k => parseMonthToValue(k)).filter(v => v >= MONTHLY_START_YYYYMM && v <= MONTHLY_END_YYYYMM)
            const allMonths: string[] = []
            if (monthVals.length > 0) {
                const minVal = MONTHLY_START_YYYYMM
                const maxVal = Math.max(...monthVals)
                for (let ym = minVal; ym <= maxVal;) {
                    const y = Math.floor(ym / 100)
                    const m = ym % 100
                    allMonths.push(`${monthNames[m - 1]} ${y}`)
                    if (m === 12) {
                        ym = (y + 1) * 100 + 1
                    } else {
                        ym = y * 100 + (m + 1)
                    }
                }
            }

            expandedCategories = [...baselineKeys.sort(), ...allMonths]
        } else if (timePeriod === 'quarterly') {
            const regularLabels = regularKeys.sort((a, b) => quarterlySortKey(a) - quarterlySortKey(b))
            expandedCategories = [...baselineKeys.sort(), ...expandQuarterlyCategories(regularLabels)]
        } else if (timePeriod === 'bi-yearly') {
            const regularLabels = regularKeys.sort((a, b) => biYearlySortKey(a) - biYearlySortKey(b))
            expandedCategories = [...baselineKeys.sort(), ...expandBiYearlyCategories(regularLabels)]
        } else {
            const regularLabels = regularKeys.sort((a, b) => yearlySortKey(a) - yearlySortKey(b))
            expandedCategories = [...baselineKeys.sort(), ...expandYearlyCategories(regularLabels)]
        }

        const anxietySeries: (number | null)[] = []
        const depressionSeries: (number | null)[] = []

        expandedCategories.forEach(period => {
            const d = aggregatedData[period]
            if (!d || d.denominator === 0) {
                anxietySeries.push(null)
                depressionSeries.push(null)
                return
            }

            const anxPct = (d.anxietyNum / d.denominator) * 100
            const depPct = (d.depressionNum / d.denominator) * 100

            anxietySeries.push(Math.round(anxPct * 100) / 100)
            depressionSeries.push(Math.round(depPct * 100) / 100)
        })

        setScreeningChartData({
            categories: expandedCategories,
            series: [
                { name: 'Anxiety Screening', data: anxietySeries },
                { name: 'Depression Screening', data: depressionSeries }
            ]
        })
    }, [rawScreeningData, timePeriod, isMultiSelect]);

    const isAllOthersValue = (v: string) => v === 'BIPOC';
    const isNHWhiteValue = (v: string) => v === 'NH White';

    const handleRaceToggle = (race: string, checked: boolean) => {
        const isBIPOC = isAllOthersValue(race);
        const isNHWhite = isNHWhiteValue(race);
        const allOthersActive = selectedRaceGroups.some(isAllOthersValue);

        if (checked) {
            if (isBIPOC) {
                const nhSelected = selectedRaceGroups.some(isNHWhiteValue);
                setSelectedRaceGroups(nhSelected ? ['BIPOC', 'NH White'] : ['BIPOC']);
                return;
            }
            if (allOthersActive && !isNHWhite) return;
            if (selectedRaceGroups.length >= BASE_MAX_NTSV_RACE_SELECTIONS) return;
            if (!selectedRaceGroups.includes(race)) {
                setSelectedRaceGroups([...selectedRaceGroups, race]);
            }
        } else {
            if (isBIPOC) {
                setSelectedRaceGroups([...DEFAULT_NTSV_RACE_SELECTIONS]);
                return;
            }
            setSelectedRaceGroups(selectedRaceGroups.filter(r => r !== race));
        }
    };

    const filterRowsByTimePeriod = (rows: any[], period: string, typeColIndex: number) => {
        return rows.filter((row: any) => {
            const reportType = row[typeColIndex];
            switch (period) {
                case 'monthly': return reportType === 'Monthly';
                case 'quarterly': return reportType === 'Quarterly';
                case 'bi-yearly': return reportType === 'Bi-Yearly';
                case 'yearly': return reportType === 'Yearly';
                default: return reportType === 'Monthly';
            }
        });
    };

    const sortRows = (rows: any[], period: string, periodColIndex: number) => {
        return rows.sort((a: any, b: any) => {
            const periodA = a[periodColIndex];
            const periodB = b[periodColIndex];

            if (period === 'monthly') {
                return parseMonthToValue(periodA) - parseMonthToValue(periodB);
            } else if (period === 'quarterly') {
                return quarterlySortKey(periodA) - quarterlySortKey(periodB);
            } else if (period === 'bi-yearly') {
                return biYearlySortKey(periodA) - biYearlySortKey(periodB);
            } else if (period === 'yearly') {
                return yearlySortKey(periodA) - yearlySortKey(periodB);
            }
            return 0;
        });
    }

    const processSingleSelectData = (data: any, period: string) => {
        if (!data) return;
        const rows = data.response?.result?.rows || [];
        const columns = data.response?.result?.column_order || [];

        const getColIndex = (name: string) => columns.indexOf(name);

        const reportTypeIdx = getColIndex('report_type') !== -1 ? getColIndex('report_type') : 14;
        let filtered = filterRowsByTimePeriod(rows, period, reportTypeIdx);

        const periodColIdx = getColIndex('period') !== -1 ? getColIndex('period') : 1;
        if (period === 'monthly') {
            filtered = filtered.filter((r: any) => {
                const mv = parseMonthToValue(r[periodColIdx]);
                return mv >= MONTHLY_START_YYYYMM && mv <= MONTHLY_END_YYYYMM;
            });
        } else if (period === 'quarterly' || period === 'bi-yearly' || period === 'yearly') {
            filtered = filtered.filter((r: any) =>
                periodOnOrAfterSep2025(period, r[periodColIdx])
            );
        }

        filtered = sortRows(filtered, period, periodColIdx);

        let categories: string[];
        let rowByPeriod: Map<string, any>;
        if (period === 'quarterly' || period === 'bi-yearly' || period === 'yearly') {
            rowByPeriod = new Map<string, any>();
            for (const r of filtered) {
                const key = canonicalPeriodLabel(period, r[periodColIdx]);
                if (!rowByPeriod.has(key)) rowByPeriod.set(key, r);
            }
            const existingLabels = Array.from(rowByPeriod.keys()).sort((a, b) => {
                if (period === 'quarterly') return quarterlySortKey(a) - quarterlySortKey(b);
                if (period === 'bi-yearly') return biYearlySortKey(a) - biYearlySortKey(b);
                return yearlySortKey(a) - yearlySortKey(b);
            });
            if (period === 'quarterly') categories = expandQuarterlyCategories(existingLabels);
            else if (period === 'bi-yearly') categories = expandBiYearlyCategories(existingLabels);
            else categories = expandYearlyCategories(existingLabels);
        } else {
            categories = filtered.map((r: any) => r[periodColIdx]);
            rowByPeriod = null as unknown as Map<string, any>;
        }

        const colNTSV = getColIndex('NTSV_Cesarean_Rate') !== -1 ? getColIndex('NTSV_Cesarean_Rate') : 9;
        const colPrivate = getColIndex('Private') !== -1 ? getColIndex('Private') : 7;
        const colPublic = getColIndex('Public') !== -1 ? getColIndex('Public') : 8;
        const colUninsured = getColIndex('Uninsured') !== -1 ? getColIndex('Uninsured') : 13;
        const colUnknown = getColIndex('Unknown_Insurance') !== -1 ? getColIndex('Unknown_Insurance') :
            (getColIndex('Unknown') !== -1 ? getColIndex('Unknown') : 10);
        const colSDOH = getColIndex('SDOH_Linkage') !== -1 ? getColIndex('SDOH_Linkage') :
            (getColIndex('Sdoh_Linkage') !== -1 ? getColIndex('Sdoh_Linkage') : 10);

        const pick = (colIdx: number) =>
            rowByPeriod
                ? categories.map((cat) => {
                    const r = rowByPeriod!.get(cat);
                    return r ? safeNumber(r[colIdx]) : null;
                })
                : filtered.map((r: any) => safeNumber(r[colIdx]));

        const series1 = [{ name: 'NTSV Cesarean Rate %', data: pick(colNTSV) }];

        const series3 = [
            { name: 'Private', data: pick(colPrivate) },
            { name: 'Public', data: pick(colPublic) },
            { name: 'Uninsured', data: pick(colUninsured) },
            { name: 'Unknown Insurance', data: pick(colUnknown) }
        ];

        const series4 = [{ name: 'SDOH Linkage', data: pick(colSDOH) }];

        setChartData((prev: any) => ({
            ...prev,
            chart1: { categories, series: series1 },
            chart3: { categories, series: series3 },
            chart4: { categories, series: series4 }
        }));
    };

    const processMultiSelectData = (data: any, period: string) => {
        if (!data) return;
        const rows = data.response?.result?.rows || [];
        const columns = data.response?.result?.column_order || [];

        const getColIndex = (name: string) => columns.indexOf(name);

        const reportTypeIdx = getColIndex('report_type') !== -1 ? getColIndex('report_type') : 14;
        const periodColIdx = getColIndex('period') !== -1 ? getColIndex('period') : 1;
        const colNTSV = getColIndex('NTSV_Cesarean_Rate') !== -1 ? getColIndex('NTSV_Cesarean_Rate') : 9;
        const colPrivate = getColIndex('Private') !== -1 ? getColIndex('Private') : 7;
        const colPublic = getColIndex('Public') !== -1 ? getColIndex('Public') : 8;
        const colUninsured = getColIndex('Uninsured') !== -1 ? getColIndex('Uninsured') : 13;
        const colUnknown = getColIndex('Unknown_Insurance') !== -1 ? getColIndex('Unknown_Insurance') :
            (getColIndex('Unknown') !== -1 ? getColIndex('Unknown') : 10);
        const colSDOH = getColIndex('SDOH_Linkage') !== -1 ? getColIndex('SDOH_Linkage') :
            (getColIndex('Sdoh_Linkage') !== -1 ? getColIndex('Sdoh_Linkage') : 10);

        let filtered = filterRowsByTimePeriod(rows, period, reportTypeIdx);

        if (period === 'monthly') {
            filtered = filtered.filter((r: any) => {
                const mv = parseMonthToValue(r[periodColIdx]);
                return mv >= MONTHLY_START_YYYYMM && mv <= MONTHLY_END_YYYYMM;
            });
        } else if (period === 'quarterly' || period === 'bi-yearly' || period === 'yearly') {
            filtered = filtered.filter((r: any) =>
                periodOnOrAfterSep2025(period, r[periodColIdx])
            );
        }

        const grouped: { [key: string]: any[] } = {};
        filtered.forEach((r: any) => {
            const key = canonicalPeriodLabel(period, r[periodColIdx]);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(r);
        });

        const uniquePeriods = Object.keys(grouped);
        let sortedPeriods = sortRows(uniquePeriods.map(p => [null, p]), period, 1).map((r: any) => r[1]);

        if (period === 'quarterly') sortedPeriods = expandQuarterlyCategories(sortedPeriods);
        else if (period === 'bi-yearly') sortedPeriods = expandBiYearlyCategories(sortedPeriods);
        else if (period === 'yearly') sortedPeriods = expandYearlyCategories(sortedPeriods);

        const avg = (items: any[], idx: number) => {
            if (!items || items.length === 0) return null;
            const values = items.map(i => parseFloat(i[idx]));
            const valid = values.filter(v => !isNaN(v));
            if (valid.length === 0) return null;
            const sum = valid.reduce((a, b) => a + b, 0);
            return sum / items.length;
        };

        const avgForPeriod = (p: string, idx: number) => (grouped[p] ? avg(grouped[p], idx) : null);

        const series1 = [{ name: 'NTSV Cesarean Rate %', data: sortedPeriods.map(p => avgForPeriod(p, colNTSV)) }];

        const series3 = [
            { name: 'Private', data: sortedPeriods.map(p => avgForPeriod(p, colPrivate)) },
            { name: 'Public', data: sortedPeriods.map(p => avgForPeriod(p, colPublic)) },
            { name: 'Uninsured', data: sortedPeriods.map(p => avgForPeriod(p, colUninsured)) },
            { name: 'Unknown Insurance', data: sortedPeriods.map(p => avgForPeriod(p, colUnknown)) }
        ];

        const series4 = [{ name: 'SDOH Linkage', data: sortedPeriods.map(p => avgForPeriod(p, colSDOH)) }];

        setChartData((prev: any) => ({
            ...prev,
            chart1: { categories: sortedPeriods, series: series1 },
            chart3: { categories: sortedPeriods, series: series3 },
            chart4: { categories: sortedPeriods, series: series4 }
        }));
    };

    const processTableData = (data: any, period: string) => {
        const rows = data.response?.result?.rows || [];
        let filtered = filterRowsByTimePeriod(rows, period, 9);

        if (period === 'monthly') {
            filtered = filtered.filter((r: any) => {
                const mv = parseMonthToValue(r[8]);
                return mv >= MONTHLY_START_YYYYMM && mv <= MONTHLY_END_YYYYMM;
            });
        } else if (period === 'quarterly' || period === 'bi-yearly' || period === 'yearly') {
            filtered = filtered.filter((r: any) =>
                periodOnOrAfterSep2025(period, r[8])
            );
        }

        filtered = filtered.sort((a: any, b: any) => {
            const hospitalA = String(a[2]);
            const hospitalB = String(b[2]);
            if (hospitalA !== hospitalB) {
                return hospitalA.localeCompare(hospitalB);
            }

            let periodCmp = 0;
            if (period === 'monthly') {
                periodCmp = parseMonthToValue(a[8]) - parseMonthToValue(b[8]);
            } else if (period === 'quarterly') {
                periodCmp = quarterlySortKey(a[8]) - quarterlySortKey(b[8]);
            } else if (period === 'bi-yearly') {
                periodCmp = biYearlySortKey(a[8]) - biYearlySortKey(b[8]);
            } else if (period === 'yearly') {
                periodCmp = yearlySortKey(a[8]) - yearlySortKey(b[8]);
            } else {
                const labelA = String(a[8] ?? '');
                const labelB = String(b[8] ?? '');
                periodCmp = labelA.localeCompare(labelB);
            }
            if (periodCmp !== 0) return periodCmp;

            const deliveriesA = parseFloat(a[5]) || 0;
            const deliveriesB = parseFloat(b[5]) || 0;
            return deliveriesB - deliveriesA;
        });

        setTableData(filtered);
    };

    const buildY2Charts = () => {
        let ntsvPct: number | null = null;
        let priv: number | null = null, pub: number | null = null, uninsured: number | null = null, unknown: number | null = null;
        if (rawTableData) {
            const cols = rawTableData.response?.result?.column_order || [];
            const rows = rawTableData.response?.result?.rows || [];
            const gI = cols.indexOf('Group') !== -1 ? cols.indexOf('Group') : 0;
            const mI = cols.indexOf('metric') !== -1 ? cols.indexOf('metric') : 1;
            const moI = cols.indexOf('Month') !== -1 ? cols.indexOf('Month') : 8;
            const rtI = cols.indexOf('report_type') !== -1 ? cols.indexOf('report_type') : 9;
            const nuI = cols.indexOf('numerator') !== -1 ? cols.indexOf('numerator') : 5;
            const deI = cols.indexOf('denominator') !== -1 ? cols.indexOf('denominator') : 6;
            const poolGroup = (groupName: string): number | null => {
                const grp = rows.filter((r: any) =>
                    String(r[mI]).toLowerCase() === 'ntsv' && String(r[gI]).toLowerCase() === groupName.toLowerCase());
                const p = poolY2Monthly(grp, moI, rtI, nuI, deI);
                return p ? p.pct : null;
            };
            ntsvPct = poolGroup('Total');
            priv = poolGroup('Private Insurance');
            pub = poolGroup('Public Insurance');
            uninsured = poolGroup('Uninsured/Self-Pay');
            unknown = poolGroup('Unknown Insurance');
        }

        let sdohPct: number | null = null;
        if (rawChartData) {
            const cols = rawChartData.response?.result?.column_order || [];
            const rows = rawChartData.response?.result?.rows || [];
            const idx = (name: string, fallback: number) => { const i = cols.indexOf(name); return i !== -1 ? i : fallback; };
            const moI = idx('Month', 1);
            const rtI = idx('report_type', 14);
            const sdohPooled = poolY2Monthly(rows, moI, rtI, idx('SDOH_Numerator', 15), idx('SDOH_Denominator', 16));
            sdohPct = sdohPooled ? sdohPooled.pct : null;
        }

        setChartData((prev: any) => ({
            ...prev,
            chart1: { categories: Y2_CATEGORIES, series: [{ name: 'NTSV Cesarean Rate %', data: y2Data(ntsvPct) }] },
            chart3: {
                categories: Y2_CATEGORIES, series: [
                    { name: 'Private', data: y2Data(priv) },
                    { name: 'Public', data: y2Data(pub) },
                    { name: 'Uninsured', data: y2Data(uninsured) },
                    { name: 'Unknown Insurance', data: y2Data(unknown) }
                ]
            },
            chart4: { categories: Y2_CATEGORIES, series: [{ name: 'SDOH Linkage', data: y2Data(sdohPct) }] }
        }));
    };

    const buildY2Table = () => {
        if (!rawTableData) { setTableData([]); return; }
        const cols = rawTableData.response?.result?.column_order || [];
        const rows = rawTableData.response?.result?.rows || [];
        const gI = cols.indexOf('Group') !== -1 ? cols.indexOf('Group') : 0;
        const mI = cols.indexOf('metric') !== -1 ? cols.indexOf('metric') : 1;
        const hidI = cols.indexOf('hospital_id') !== -1 ? cols.indexOf('hospital_id') : 2;
        const hnI = cols.indexOf('hospital_name') !== -1 ? cols.indexOf('hospital_name') : 3;
        const moI = cols.indexOf('Month') !== -1 ? cols.indexOf('Month') : 8;
        const rtI = cols.indexOf('report_type') !== -1 ? cols.indexOf('report_type') : 9;
        const nuI = cols.indexOf('numerator') !== -1 ? cols.indexOf('numerator') : 5;
        const deI = cols.indexOf('denominator') !== -1 ? cols.indexOf('denominator') : 6;

        const acc: Record<string, { hid: string; hn: string; metric: string; group: string; num: number; den: number }> = {};
        rows.forEach((r: any) => {
            if (String(r[rtI]) !== 'Monthly') return;
            const mv = parseMonthToValue(r[moI]);
            if (mv < Y2_START_YYYYMM || mv > Y2_END_YYYYMM) return;
            const d = safeNumber(r[deI]);
            if (d === null) return;
            const n = safeNumber(r[nuI]);
            const key = `${r[hidI]}|${r[mI]}|${r[gI]}`;
            if (!acc[key]) acc[key] = { hid: String(r[hidI]), hn: String(r[hnI]), metric: String(r[mI]), group: String(r[gI]), num: 0, den: 0 };
            acc[key].den += d;
            if (n !== null) acc[key].num += n;
        });

        const out = Object.values(acc).map(g => {
            const row: any[] = [];
            row[0] = g.group;
            row[1] = g.metric;
            row[2] = g.hid;
            row[3] = g.hn;
            row[5] = g.num;
            row[6] = g.den;
            row[7] = g.den > 0 ? Math.round((g.num / g.den) * 100 * 100) / 100 : 0;
            row[8] = Y2_LABEL;
            row[9] = 'Monthly';
            return row;
        });

        out.sort((a, b) => {
            const h = String(a[2]).localeCompare(String(b[2]));
            if (h !== 0) return h;
            return (parseFloat(b[5]) || 0) - (parseFloat(a[5]) || 0);
        });

        setTableData(out);
    };

   const handleDownloadPNG = async (ref: React.RefObject<HTMLDivElement>, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-ignore')
            }as any);
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        }
    };

    const handleDownloadSVG = async (ref: React.RefObject<HTMLDivElement>, title: string | undefined, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-menu') || el.classList.contains('export-dropdown')   || el.classList.contains("export-ignore2")
            }as any);

            const width = canvas.width / 2;
            const height = canvas.height / 2;
            const dataURL = canvas.toDataURL('image/png');

            let svgTitleElement = '';
            let svgHeight = height;

            if (title) {
                const escapedTitle = title
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;');

                svgTitleElement = `<text x="${width / 2}" y="25" text-anchor="middle" class="chart-title">${escapedTitle}</text>`;
                svgHeight = height + 40;
            }

            const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${svgHeight}" viewBox="0 0 ${width} ${svgHeight}">
  <style>
    .chart-title {
      font-family: 'Segoe UI', sans-serif;
      font-size: 16px;
      fill: #1a2a44;
      font-weight: bold;
    }
  </style>
  <rect width="100%" height="100%" fill="white"/>
  ${svgTitleElement}
  <image x="0" y="${title ? 40 : 0}" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;

            const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.svg`;
            link.click();
            URL.revokeObjectURL(url);
        }
    };
 const NTSVCesareanRateContainerRef = React.useRef<HTMLDivElement>(null)
 const NTSVcesareanStratifiedContainerRef = React.useRef<HTMLDivElement>(null)
 const chart3containerRef = React.useRef<HTMLDivElement>(null)
 const chart4ContainerRef = React.useRef<HTMLDivElement>(null)
 const chart5ContainerRef = React.useRef<HTMLDivElement>(null)
    const handleNTSVCSVDownload = () => {
        const ntsvData = tableData.filter(row => row[1] === 'ntsv');
        if (ntsvData.length === 0) return;

        const safeVal = (v: any) => v !== null && v !== undefined && v !== '' ? v : 0;
        exportToCSV({
            headers: ['Hospital ID', 'Hospital Group Name', 'Month', 'Group', 'NTSVs Resulting in Cesarean Deliveries', 'NTSV Deliveries', 'NTSV Cesarean Rate'],
            rows: ntsvData.map(row => [
                row[2] || '', `${row[2]} - ${row[3]}`, row[8] || '', row[0] || '',
                safeVal(row[5]), safeVal(row[6]), safeVal(row[7])
            ]),
            filename: 'ntsv_deliveries_csection'
        });
    };

    const handleSDOHCSVDownload = () => {
        const sdohData = tableData.filter(row => row[1] === 'sdoh');
        if (sdohData.length === 0) return;

        const safeVal = (v: any) => v !== null && v !== undefined && v !== '' ? v : 0;
        exportToCSV({
            headers: ['Hospital ID', 'Hospital Group Name', 'Month', 'Group', 'Patient Linked', 'Patients Screening Positive for SDoH', 'SDOH Linkage (%)'],
            rows: sdohData.map(row => [
                row[2] || '', `${row[2]} - ${row[3]}`, row[8] || '', row[0] || '',
                safeVal(row[5]), safeVal(row[6]), safeVal(row[7])
            ]),
            filename: 'sdoh_screening_linkage'
        });
    };

    const handleDownloadNTSVRaceCSV = () => {
        const { categories, series } = chartData.chart2 || {};
        if (!categories || categories.length === 0) return;

        const hospitalIds = selectedHospitalIds.join(', ');
        const seriesNames = series.map((s: any) => s.name);
        const header = ['Hospital ID(s)', 'Month', ...seriesNames];
        const dataRows = categories
            .map((cat: string, i: number) => ({ cat, i }))
            .filter(({ cat }: { cat: string }) => String(cat).trim() !== '')
            .map(({ cat, i }: { cat: string; i: number }) => {
                const values = series.map((s: any) => s.data[i] !== null && s.data[i] !== undefined ? s.data[i] : '');
                return [`"${hospitalIds}"`, cat, ...values];
            });
        const csvContent = [header, ...dataRows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'ntsv_cesarean_rate_race_ethnicity.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleChartCSVDownload = (chartKey: string, filename: string) => {
        const { categories, series } = chartData[chartKey] || {}
        if (!categories || categories.length === 0) return

        const seriesNames = series.map((s: any) => s.name)
        exportToCSV({
            headers: ['Month', ...seriesNames],
            rows: categories
                .map((cat: string, i: number) => ({ cat, i }))
                .filter(({ cat }: { cat: string }) => String(cat).trim() !== '')
                .map(({ cat, i }: { cat: string; i: number }) =>
                    [cat, ...series.map((s: any) => s.data[i] !== null && s.data[i] !== undefined ? s.data[i] : '')]
                ),
            filename
        })
    }

    const handleScreeningCSVDownload = () => {
        const { categories, series } = screeningChartData || {}
        if (!categories || categories.length === 0) return

        const seriesNames = series.map((s: any) => s.name)
        exportToCSV({
            headers: ['Month', ...seriesNames],
            rows: categories
                .map((cat: string, i: number) => ({ cat, i }))
                .filter(({ cat }: { cat: string }) => String(cat).trim() !== '')
                .map(({ cat, i }: { cat: string; i: number }) =>
                    [cat, ...series.map((s: any) => s.data[i] !== null && s.data[i] !== undefined ? s.data[i] : '')]
                ),
            filename: 'pmh_screening'
        })
    }

    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId);
        if (menu) menu.style.display = 'none';
    };

    if (initialLoading) {
        return <LoadingNotice title="Loading outcome measures data..." />;
    }

    const bipocActive = selectedRaceGroups.some(isAllOthersValue);
    const raceChartColors = selectedRaceGroups.map(g => {
        if (bipocActive && isAllOthersValue(g)) return '#0072B1';
        if (bipocActive && isNHWhiteValue(g))   return '#F5EC77';
        return NTSV_RACE_COLORS[g] || '#888888';
    });

    return (
        <div id={generatePDFIds.dashboard()} className="bcbs-outcome-measures-page">
            <div className="page-header">
                <h1 className="page-title">Outcome Measures</h1>
                <div className="controls">
                    <CustomSelect
                        value={timePeriod}
                        onChange={setTimePeriod}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' },
                            { value: 'y2', label: 'Y2 Designation Year' }
                        ]}
                    />
                </div>
            </div>

            <div className="chart-grid">
                <div id={generatePDFIds.chart(1)} className="chart-container" ref={NTSVCesareanRateContainerRef }>
                    <div className="chart-header-row">
                        <h2 className="chart-title">Achieve NTSV Cesarean Rate at or below 25%</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-outcome-1');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-outcome-1" className="export-dropdown-menu">
                                <div onClick={() => { handleDownloadSVG(NTSVCesareanRateContainerRef , undefined,"Achieve Healthy People NTSV Cesarean Rate at or below 23.6%"); closeMenu('export-dropdown-menu-outcome-1'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(NTSVCesareanRateContainerRef , "Achieve Healthy People NTSV Cesarean Rate at or below 23.6%"); closeMenu('export-dropdown-menu-outcome-1'); }}>Download PNG</div>
                                <div onClick={() => { handleChartCSVDownload('chart1', 'ntsv_cesarean_rate'); closeMenu('export-dropdown-menu-outcome-1'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart1Ref}
                        categories={chartData.chart1.categories}
                        series={chartData.chart1.series}
                        goalLine={25}
                        goalLabel="Goal ≤ 25%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={timePeriod === 'y2'}
                        yAxisLabel="NTSV Cesarean Rate %"
                        colors={["rgb(86,180,233)"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="right"
                        showXAxisTooltip={true}
                        exportFilename="Achieve NTSV Cesarean Rate at or below 25%"
                        chartTitle="Achieve NTSV Cesarean Rate at or below 25%"
                        pageTitle="Outcome Measures"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                    />
                </div>

                <div id={generatePDFIds.chart(2)} className="chart-container" ref={NTSVcesareanStratifiedContainerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">Achieve NTSV Cesarean Rate by Race/Ethnicity at or below 25%</h2>
                        <div className="chart-header-actions export-ignore export-ignore2">
                            <TmbPillSelect
                                displayText={
                                    selectedRaceGroups.length === 0
                                        ? 'Race/Ethnicity'
                                        : selectedRaceGroups.length === 1
                                            ? selectedRaceGroups[0]
                                            : `${selectedRaceGroups.length} selected`
                                }
                                variant="multiselect"
                                isOpen={raceDropdownOpen}
                                onToggle={setRaceDropdownOpen}
                            >
                                {(() => {
                                    const bipocItem = availableRaceGroups.find(r => isAllOthersValue(r));
                                    const nhWhiteItem = availableRaceGroups.find(r => isNHWhiteValue(r));
                                    const subItems = availableRaceGroups.filter(r => !isAllOthersValue(r) && !isNHWhiteValue(r));
                                    const allOthersActive = selectedRaceGroups.some(isAllOthersValue);
                                    const isMaxSelected = selectedRaceGroups.length >= BASE_MAX_NTSV_RACE_SELECTIONS;

                                    const renderRaceItem = (race: string) => {
                                        const isChecked = selectedRaceGroups.includes(race);
                                        const isBIPOC = isAllOthersValue(race);
                                        const isNH = isNHWhiteValue(race);
                                        const isLockedChecked = allOthersActive && !isBIPOC && !isNH;
                                        const isDisabled = isLockedChecked || (!isChecked && isMaxSelected && !isBIPOC);
                                        return (
                                            <label
                                                key={race}
                                                className={`prem-race-item${isDisabled ? ' disabled' : ''}${isLockedChecked ? ' locked-checked' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="prem-race-checkbox"
                                                    checked={isLockedChecked ? true : isChecked}
                                                    onChange={(e) => handleRaceToggle(race, e.target.checked)}
                                                    disabled={isDisabled}
                                                />
                                                {race}
                                                {isBIPOC && (
                                                    <span className="prem-race-info" title="Select to view an aggregated comparison of all other race/ethnicity groups.">i</span>
                                                )}
                                            </label>
                                        );
                                    };
                                    return (
                                        <>
                                            {bipocItem && renderRaceItem(bipocItem)}
                                            {subItems.length > 0 && (
                                                <div className="prem-race-sub-options">
                                                    {subItems.map(race => renderRaceItem(race))}
                                                </div>
                                            )}
                                            {nhWhiteItem && renderRaceItem(nhWhiteItem)}
                                        </>
                                    );
                                })()}
                            </TmbPillSelect>
                            <div className="export-menu">
                                <button className="hamburger-btn" onClick={() => {
                                    const menu = document.getElementById('export-dropdown-menu-outcome-2');
                                    if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                                }}>&#9776;</button>
                                <div id="export-dropdown-menu-outcome-2" className="export-dropdown-menu">
                                    <div onClick={() => { handleDownloadSVG(NTSVcesareanStratifiedContainerRef, undefined, "Achieve NTSV Cesarean Rate by Race/Ethnicity at or below 25%"); closeMenu('export-dropdown-menu-outcome-2'); }}>Download SVG</div>
                                    <div onClick={() => { handleDownloadPNG(NTSVcesareanStratifiedContainerRef, "Achieve NTSV Cesarean Rate by Race/Ethnicity at or below 25%"); closeMenu('export-dropdown-menu-outcome-2'); }}>Download PNG</div>
                                    <div onClick={() => { handleDownloadNTSVRaceCSV(); closeMenu('export-dropdown-menu-outcome-2'); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart2Ref}
                        categories={chartData.chart2.categories}
                        series={chartData.chart2.series}
                        goalLine={25}
                        goalLabel="Goal ≤ 25%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={timePeriod === 'y2'}
                        yAxisLabel="NTSV Cesarean Rate %"
                        colors={raceChartColors}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="center"
                        showXAxisTooltip={true}
                        exportFilename="Achieve NTSV Cesarean Rate by Race/Ethnicity at or below 25%"
                        chartTitle="Achieve NTSV Cesarean Rate by Race/Ethnicity at or below 25%"
                        pageTitle="Outcome Measures"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                    />
                </div>

                <div id={generatePDFIds.chart(3)} className="chart-container" ref={chart3containerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-outcome-3');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-outcome-3" className="export-dropdown-menu">
                                <div onClick={() => {handleDownloadSVG(chart3containerRef,undefined, 'Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance'); closeMenu('export-dropdown-menu-outcome-3'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(chart3containerRef, 'Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance'); closeMenu('export-dropdown-menu-outcome-3'); }}>Download PNG</div>
                                <div onClick={() => { handleChartCSVDownload('chart3', 'ntsv_cesarean_rate_insurance'); closeMenu('export-dropdown-menu-outcome-3'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart3Ref}
                        categories={chartData.chart3.categories}
                        series={chartData.chart3.series}
                        goalLine={25}
                        goalLabel="Goal ≤ 25%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={timePeriod === 'y2'}
                        yAxisLabel="NTSV Cesarean Rate"
                        colors={["rgb(0,114,178)", "rgb(230,159,0)", "rgb(204,121,167)", "rgb(213,94,0)"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="center"
                        showXAxisTooltip={true}
                        exportFilename="Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance"
                        chartTitle="Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance"
                        pageTitle="Outcome Measures"
                        isLoading={loading}
                        showZeroLine={true}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                    />
                </div>
            </div>

            <div className="tables-section">
                <div className="table-card">
                    <div className="table-header-row">
                        <h3 className="table-title">NTSV Deliveries and C-section Deliveries</h3>
                        <div className="export-menu">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-ntsv');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-ntsv" className="export-dropdown-menu">
                                <div onClick={() => {
                                    handleNTSVCSVDownload();
                                    const menu = document.getElementById('export-dropdown-menu-ntsv');
                                    if (menu) menu.style.display = 'none';
                                }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bcbs-table-container" ref={tableRef}>
                        <table className="hospital-table">
                            <thead>
                                <tr>
                                    <th>Hospital ID</th>
                                    <th>Hospital Group Name</th>
                                    <th>Month</th>
                                    <th>Group</th>
                                    <th>NTSVs Resulting in Cesarean Deliveries</th>
                                    <th>NTSV Deliveries</th>
                                    <th>NTSV Cesarean Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData
                                    .filter(row => row[1] === 'ntsv')
                                    .map((row, idx) => {
                                        const isTotal = row[0]?.toLowerCase() === 'total';
                                        return (
                                            <tr
                                                key={idx}
                                                style={{
                                                    fontWeight: isTotal ? 'bold' : 'normal'
                                                }}
                                            >
                                                <td>{row[2]}</td>
                                                <td>{row[2]} - {row[3]}</td>
                                                <td>{row[8]}</td>
                                                <td>{row[0]}</td>
                                                <td>{row[5] !== null && row[5] !== undefined && row[5] !== '' ? row[5] : 0}</td>
                                                <td>{row[6] !== null && row[6] !== undefined && row[6] !== '' ? row[6] : 0}</td>
                                                <td>{row[7] !== null && row[7] !== undefined && row[7] !== '' ? row[7] : 0}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div id={generatePDFIds.chart(4)} className="chart-container" ref={chart4ContainerRef}>
                <div className="chart-header-row">
                    <h2 className="chart-title">Link patients who screen positive for social determinant of health needs</h2>
                    <div className="export-menu export-ignore">
                        <button className="hamburger-btn" onClick={() => {
                            const menu = document.getElementById('export-dropdown-menu-outcome-4');
                            if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                        }}>&#9776;</button>
                        <div id="export-dropdown-menu-outcome-4" className="export-dropdown-menu">
                            <div onClick={() => { handleDownloadSVG(chart4ContainerRef,undefined,"Link patients who screen positive for social determinant of health needs"); closeMenu('export-dropdown-menu-outcome-4'); }}>Download SVG</div>
                            <div onClick={() => { handleDownloadPNG(chart4ContainerRef,"Link patients who screen positive for social determinant of health needs"); closeMenu('export-dropdown-menu-outcome-4'); }}>Download PNG</div>
                            <div onClick={() => { handleChartCSVDownload('chart4', 'sdoh_linkage'); closeMenu('export-dropdown-menu-outcome-4'); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <LineChart
                    ref={chart4Ref}
                    categories={chartData.chart4.categories}
                    series={chartData.chart4.series}
                    goalLine={75}
                    goalLabel="Goal ≥ 75%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    markers={timePeriod === 'y2'}
                    yAxisLabel="SDOH Linkage (% charts with needs identified)"
                    colors={["#00AAFF"]}
                    fontFamily="Helvetica, Arial, sans-serif"
                    legendPosition="top"
                    legendAlign="right"
                    showXAxisTooltip={true}
                    exportFilename="Link patients who screen positive for social determinant of health needs"
                    chartTitle="Link patients who screen positive for social determinant of health needs"
                    pageTitle="Outcome Measures"
                    isLoading={loading}
                    yAxisDecimals={1}
                    decimals={2}
                    xAxisLabelFontSize="14px"
                />
            </div>

            <div className="tables-section">
                <div className="table-card">
                    <div className="table-header-row">
                        <h3 className="table-title">Social Determinants of Health Screening and Linkage</h3>
                        <div className="export-menu">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-sdoh');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-sdoh" className="export-dropdown-menu">
                                <div onClick={() => {
                                    handleSDOHCSVDownload();
                                    const menu = document.getElementById('export-dropdown-menu-sdoh');
                                    if (menu) menu.style.display = 'none';
                                }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                      <div className="bcbs-table-container">
                        <table className="hospital-table">
                            <thead>
                                <tr>
                                    <th>Hospital ID</th>
                                    <th>Hospital Group Name</th>
                                    <th>Month</th>
                                    <th>Group</th>
                                    <th>Patient Linked</th>
                                    <th>Patients Screening Positive for SDoH</th>
                                    <th>SDOH Linkage (%)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData
                                    .filter(row => row[1] === 'sdoh')
                                    .map((row, idx) => {
                                        return (
                                            <tr key={idx}>
                                                <td>{row[2]}</td>
                                                <td>{row[2]} - {row[3]}</td>
                                                <td>{row[8]}</td>
                                                <td>{row[0]}</td>
                                                <td>{row[5] !== null && row[5] !== undefined && row[5] !== '' ? row[5] : 0}</td>
                                                <td>{row[6] !== null && row[6] !== undefined && row[6] !== '' ? row[6] : 0}</td>
                                                <td>{row[7] !== null && row[7] !== undefined && row[7] !== '' ? row[7] : 0}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div id={generatePDFIds.chart(5)} className="chart-container" ref={chart5ContainerRef}>
                <div className="chart-header-row">
                    <h2 className="chart-title">PMH Screening</h2>
                    <div className="export-menu export-ignore">
                        <button className="hamburger-btn" onClick={() => {
                            const menu = document.getElementById('export-dropdown-menu-outcome-5');
                            if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                        }}>&#9776;</button>
                        <div id="export-dropdown-menu-outcome-5" className="export-dropdown-menu">
                            <div onClick={() => { handleDownloadSVG(chart5ContainerRef, undefined, "PMH Screening"); closeMenu('export-dropdown-menu-outcome-5'); }}>Download SVG</div>
                            <div onClick={() => { handleDownloadPNG(chart5ContainerRef, "PMH Screening"); closeMenu('export-dropdown-menu-outcome-5'); }}>Download PNG</div>
                            <div onClick={() => { handleScreeningCSVDownload(); closeMenu('export-dropdown-menu-outcome-5'); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p style={{ fontSize: '14px', color: '#555', margin: '0 0 1rem 1.2rem' }}>
                    Percent of sample patient charts that had documentation of PMH screening using a validated tool.
                </p>
                <LineChart
                    ref={chart5Ref}
                    categories={screeningChartData.categories}
                    series={screeningChartData.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    goalLabelOffsetX={0}
                    min={0}
                    max={100}
                    tickAmount={20}
                    markers={timePeriod === 'y2'}
                    yAxisLabel="PMH Screening (%)"
                    xAxisLabel="Time Period"
                    colors={["#0072B2", "#D55E00"]}
                    fontFamily="Helvetica, Arial, sans-serif"
                    legendPosition="top"
                    legendAlign="center"
                    showXAxisTooltip={true}
                    noDataMessage="No Data Available"
                    exportFilename="PMH Screening"
                    chartTitle="PMH Screening"
                    chartSubtitle="Percent of sample patient charts that had documentation of PMH screening using a validated tool."
                    pageTitle="Outcome Measures"
                    isLoading={loading}
                    yAxisDecimals={1}
                    decimals={2}
                    xAxisLabelFontSize="14px"
                />
            </div>
        </div>
    );
};

export default OutcomeMeasures;
